// Apex Business Intelligence — QuickBooks Online integration page (additive)
// Owner / Admin only. Connect via OAuth, sync customers/invoices/payments.
import React, { useEffect, useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "apex_auth_token";

const auth = () => { const t = localStorage.getItem(TOKEN_KEY); return t ? { Authorization: "Bearer " + t } : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...auth(), ...(opts.headers || {}) } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(d.detail || r.statusText), { data: d, status: r.status }); return d; });

const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 18, marginBottom: 14 };
const subhead = { fontFamily: "Rajdhani, Inter, sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 10, letterSpacing: 1, color: "#475569", textTransform: "uppercase" };
const btn = { padding: "8px 14px", border: "none", background: "#dc2626", color: "#fff", fontSize: 12, fontWeight: 600, borderRadius: 4, cursor: "pointer", letterSpacing: 0.5 };
const btnAlt = { ...btn, background: "#f8fafc" };
const btnGhost = { ...btn, background: "transparent", border: "1px solid #C0C0C0", color: "#111" };
const pill = (bg) => ({ display: "inline-block", padding: "2px 10px", borderRadius: 999, fontSize: 11, background: bg, color: "#fff", fontWeight: 600 });
const tableHead = { fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "#475569", textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #f1f5f9" };
const tableCell = { fontSize: 12, padding: "6px 8px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" };

function StatusPill({ status }) {
  if (status === "connected") return <span style={pill("#16a34a")} data-testid="qb-status-pill">CONNECTED</span>;
  if (status === "requires_reauthorization") return <span style={pill("#f59e0b")} data-testid="qb-status-pill">RECONNECT REQUIRED</span>;
  return <span style={pill("#6b7280")} data-testid="qb-status-pill">NOT CONNECTED</span>;
}

export function QuickBooksPage() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [redir, setRedir] = useState(null);
  const [imported, setImported] = useState({ customers: [], invoices: [], payments: [] });
  const [tab, setTab] = useState("customers");
  const [flash, setFlash] = useState(null);

  const loadStatus = useCallback(() => {
    j("/api/iv/qb/status").then(setS).catch(() => setS({ connected: false, status: "not_connected" }));
  }, []);

  const loadImported = useCallback((kind) => {
    j(`/api/iv/qb/imported/${kind}?limit=50`)
      .then(r => setImported(prev => ({ ...prev, [kind]: r.rows || [] })))
      .catch(() => {});
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { j("/api/iv/qb/redirect-uri").then(setRedir).catch(() => {}); }, []);
  useEffect(() => { if (s?.connected) loadImported(tab); }, [tab, s?.connected, loadImported]);

  // Detect Intuit callback redirect (?qb=connected|error)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qb = params.get("qb");
      if (qb === "connected") {
        const realm = params.get("realm");
        setFlash({ kind: "ok", msg: `QuickBooks connected (realm ${realm || ""})` });
        loadStatus();
        // Strip the query so a reload doesn't repeat
        window.history.replaceState({}, "", window.location.pathname + window.location.hash);
      } else if (qb === "error") {
        setFlash({ kind: "err", msg: `QuickBooks connection failed: ${params.get("msg") || "unknown"}` });
        window.history.replaceState({}, "", window.location.pathname + window.location.hash);
      }
    } catch (e) { /* no-op */ }
  }, [loadStatus]);

  const connect = async () => {
    setBusy("connect"); setFlash(null);
    try {
      const r = await j("/api/iv/qb/authorize");
      window.location.href = r.authorization_url;
    } catch (e) {
      setFlash({ kind: "err", msg: e.message || "Failed to start OAuth" });
      setBusy("");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks? Tokens will be revoked.")) return;
    setBusy("disconnect"); setFlash(null);
    try {
      await j("/api/iv/qb/disconnect", { method: "POST" });
      setFlash({ kind: "ok", msg: "QuickBooks disconnected." });
      loadStatus();
    } catch (e) { setFlash({ kind: "err", msg: e.message }); }
    finally { setBusy(""); }
  };

  const sync = async (kind) => {
    setBusy("sync-" + kind); setFlash(null);
    try {
      const r = await j(`/api/iv/qb/sync/${kind}`, { method: "POST" });
      setFlash({ kind: "ok", msg: `Synced ${r.synced} ${kind}` });
      loadStatus(); loadImported(kind); setTab(kind);
    } catch (e) { setFlash({ kind: "err", msg: e.message }); }
    finally { setBusy(""); }
  };

  if (!s) return <div style={card}>Loading QuickBooks status…</div>;

  return (
    <div data-testid="qb-page">
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: 1 }}>QuickBooks Online</div>
            <div style={{ fontSize: 12, color: "#475569" }}>OAuth 2.0 · {redir?.environment || s.environment || "sandbox"} · read-only sync (customers · invoices · payments)</div>
          </div>
          <StatusPill status={s.status} />
        </div>
        {flash && (
          <div data-testid="qb-flash" style={{ marginTop: 12, padding: 10, borderRadius: 4,
            background: flash.kind === "ok" ? "#dcfce7" : "#fee2e2",
            color: flash.kind === "ok" ? "#166534" : "#991b1b", fontSize: 12 }}>{flash.msg}</div>
        )}
      </div>

      <div style={card}>
        <div style={subhead}>Connection</div>
        {s.connected ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, marginBottom: 12 }}>
              <div><div style={{ fontSize: 10, color: "#475569" }}>REALM ID</div><div style={{ fontFamily: "monospace" }} data-testid="qb-realm">{s.realm_id}</div></div>
              <div><div style={{ fontSize: 10, color: "#475569" }}>ENVIRONMENT</div><div>{s.environment}</div></div>
              <div><div style={{ fontSize: 10, color: "#475569" }}>ACCESS EXPIRES</div><div style={{ fontSize: 11 }}>{(s.access_token_expires_at || "").slice(0, 19).replace("T", " ")}</div></div>
              <div><div style={{ fontSize: 10, color: "#475569" }}>LAST SYNC</div><div style={{ fontSize: 11 }}>{(s.last_sync_at || "—").slice(0, 19).replace("T", " ")}</div></div>
            </div>
            <button style={btnGhost} onClick={disconnect} disabled={busy === "disconnect"} data-testid="qb-disconnect">
              {busy === "disconnect" ? "Disconnecting…" : "Disconnect"}
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: "#111", marginBottom: 10 }}>
              Connect your QuickBooks Online company to sync customers, invoices, and payments into Apex Business Intelligence.
              {s.status === "requires_reauthorization" && (
                <span style={{ color: "#92400e", fontWeight: 600, display: "block", marginTop: 4 }}>
                  Token expired or revoked — please reconnect.
                </span>
              )}
            </p>
            {redir && (
              <div style={{ fontSize: 11, color: "#475569", marginBottom: 10, background: "#f8fafc", padding: 8, borderRadius: 4 }}>
                <strong>Intuit Developer portal:</strong> ensure this exact redirect URI is whitelisted →<br/>
                <code style={{ fontSize: 11 }} data-testid="qb-redirect-uri">{redir.redirect_uri}</code>
              </div>
            )}
            <button style={btn} onClick={connect} disabled={busy === "connect" || !redir?.configured} data-testid="qb-connect">
              {busy === "connect" ? "Redirecting to Intuit…" : "Connect QuickBooks"}
            </button>
            {!redir?.configured && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 6 }}>QB_CLIENT_ID / QB_REDIRECT_URI not configured on server.</div>}
          </>
        )}
      </div>

      {s.connected && (
        <div style={card}>
          <div style={subhead}>Sync</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <button style={btnAlt} onClick={() => sync("customers")} disabled={busy.startsWith("sync")} data-testid="qb-sync-customers">
              {busy === "sync-customers" ? "Syncing…" : "Sync Customers"}
            </button>
            <button style={btnAlt} onClick={() => sync("invoices")} disabled={busy.startsWith("sync")} data-testid="qb-sync-invoices">
              {busy === "sync-invoices" ? "Syncing…" : "Sync Invoices"}
            </button>
            <button style={btnAlt} onClick={() => sync("payments")} disabled={busy.startsWith("sync")} data-testid="qb-sync-payments">
              {busy === "sync-payments" ? "Syncing…" : "Sync Payments"}
            </button>
          </div>
          {s.last_sync_counts && (
            <div style={{ fontSize: 11, color: "#475569" }}>
              Last sync counts:{" "}
              {Object.entries(s.last_sync_counts).map(([k, v]) => <span key={k} style={{ marginRight: 10 }}>{k}: <strong>{v}</strong></span>)}
            </div>
          )}
        </div>
      )}

      {s.connected && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={subhead}>Imported Records</div>
            <div style={{ display: "flex", gap: 4 }}>
              {["customers", "invoices", "payments"].map(k => (
                <button key={k} onClick={() => setTab(k)} data-testid={`qb-tab-${k}`}
                  style={{ ...btnGhost, padding: "4px 10px", fontSize: 11,
                    background: tab === k ? "#1e40af" : "transparent",
                    color: tab === k ? "#fff" : "#374151",
                    borderColor: tab === k ? "#1e40af" : "#d1d5db" }}>
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid={`qb-imported-${tab}`}>
              <thead>
                <tr>
                  <th style={tableHead}>QBO ID</th>
                  <th style={tableHead}>{tab === "customers" ? "Name" : tab === "invoices" ? "DocNumber" : "Reference"}</th>
                  <th style={tableHead}>{tab === "customers" ? "Email" : "Total"}</th>
                  <th style={tableHead}>Synced</th>
                </tr>
              </thead>
              <tbody>
                {(imported[tab] || []).map((r) => {
                  const d = r.data || {};
                  const label = tab === "customers"
                    ? (d.DisplayName || d.CompanyName || d.GivenName || "—")
                    : tab === "invoices" ? (d.DocNumber || d.Id || "—")
                    : (d.PaymentRefNum || d.Id || "—");
                  const secondary = tab === "customers"
                    ? (d.PrimaryEmailAddr?.Address || "—")
                    : `$${(d.TotalAmt ?? 0).toFixed(2)}`;
                  return (
                    <tr key={r.qbo_id}>
                      <td style={{ ...tableCell, fontFamily: "monospace" }}>{r.qbo_id}</td>
                      <td style={tableCell}>{label}</td>
                      <td style={tableCell}>{secondary}</td>
                      <td style={{ ...tableCell, color: "#475569" }}>{(r.synced_at || "").slice(0, 19).replace("T", " ")}</td>
                    </tr>
                  );
                })}
                {!(imported[tab] || []).length && (
                  <tr><td colSpan={4} style={{ ...tableCell, textAlign: "center", color: "#94a3b8" }}>
                    No {tab} synced yet. Click "Sync {tab.charAt(0).toUpperCase() + tab.slice(1)}" above.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
